// ============================================================================
// Practice Catalog — visitor-facing marketing data
// ----------------------------------------------------------------------------
// Powers /practices (grid) and /practices/:slug (detail). Slugs match the
// backend intervention `type` keys exactly so signed-in users land on the
// corresponding /intervention/<slug> page.
// ============================================================================

export const PRACTICE_CATALOG = [
  {
    slug: "quiet_the_worry",
    title: "Quiet The Worry",
    hook: "Calm anxious, looping thoughts.",
    color: "#3090A8",
    image: "/images/interventions/quiet-the-worry.webp",
    group: "Emotional grounding",
    bestFor: [
      "Recurring \"what-if\" scenarios that won't quiet down",
      "A racing mind right before sleep or during a quiet moment",
      "Anxiety about a specific situation or future event",
      "A tight chest, shallow breath, or churning stomach paired with worry",
    ],
  },
  {
    slug: "overcome_overwhelm",
    title: "Overcome Your Overwhelm",
    hook: "Untangle when too much piles up.",
    color: "#78C0D8",
    image: "/images/interventions/overcome-overwhelm.webp",
    group: "Calm body",
    bestFor: [
      "A to-do list or inbox that feels impossible to start",
      "Feeling frozen, scattered, or pulled in every direction",
      "Stress before a deadline, big week, or busy season",
      "Wanting to think clearly enough to take a single next step",
    ],
  },
  {
    slug: "dial_down_tension",
    title: "Dial Down Body Tension",
    hook: "Release tightness held in the body.",
    color: "#E2B887",
    image: "/images/interventions/dial-down-tension.webp",
    group: "Calm body",
    bestFor: [
      "Tight shoulders, clenched jaw, or knotted upper back",
      "Long days at a desk leaving your body braced",
      "Chronic low-grade physical stress with no clear cause",
      "Unwinding before bed without reaching for a screen",
    ],
  },
  {
    slug: "headache_relief",
    title: "Headache Relief",
    hook: "Ease tension headaches at the source.",
    color: "#D98880",
    image: "/images/interventions/headache-relief.webp",
    group: "Calm body",
    bestFor: [
      "Tension headaches that build behind the eyes, jaw, or neck",
      "Headaches that follow a stressful conversation or long screen day",
      "Recurring tension patterns you'd rather not medicate every time",
      "Wanting a gentle, body-based way to soften the ache",
    ],
    medicalDisclaimer:
      "Not a substitute for medical care if the headache is severe, new, or unusual. Please see a clinician if anything feels off.",
  },
  {
    slug: "healing_old_stories",
    title: "Healing Old Stories",
    hook: "Loosen the grip of past wounds.",
    color: "#D98880",
    image: "/images/interventions/healing-old-stories.webp",
    group: "Repair & transformation",
    bestFor: [
      "Memories from years ago that still sting in the body",
      "Old narratives about yourself that feel stuck on repeat",
      "Painful chapters you want to revisit at your own pace",
      "Self-help support — not a replacement for trauma therapy",
    ],
  },
  {
    slug: "beat_stage_fright",
    title: "Beat Stage Fright",
    hook: "Settle nerves before you step up.",
    color: "#9F7AEA",
    image: "/images/interventions/beat-stage-fright.webp",
    group: "Growth & confidence",
    bestFor: [
      "Presentations, performances, auditions, or pitch meetings",
      "Body-level dread that hits before you take the stage",
      "Recurring choke-ups in front of an audience",
      "Wanting to show up clear, present, and yourself",
    ],
  },
  {
    slug: "fear_of_sleeplessness",
    title: "Fear Of Sleeplessness",
    hook: "Quiet the mind so rest can come.",
    color: "#78C0D8",
    image: "/images/interventions/fear-of-sleeplessness.webp",
    group: "Sleep health",
    bestFor: [
      "Lying awake watching the clock and dreading morning",
      "Anxiety about whether tonight will be another bad night",
      "A nervous system that won't drop into rest mode",
      "Wanting a calming routine to replace the doomscroll",
    ],
  },
  {
    slug: "shake_off_sadness",
    title: "Shake Off The Blues",
    hook: "Lift a low, heavy mood.",
    color: "#3090A8",
    image: "/images/interventions/shake-off-sadness.webp",
    group: "Emotional grounding",
    bestFor: [
      "Low moods that won't quite lift on their own",
      "Heaviness in the chest, tiredness behind the eyes",
      "Numbness or a flatness that's hard to shake",
      "Self-help between therapy sessions — not a replacement for care",
    ],
  },
  {
    slug: "boost_self_worth",
    title: "Boost Your Self-Worth",
    hook: "Reclaim a steady sense of value.",
    color: "#E2B887",
    image: "/images/interventions/boost-self-worth.webp",
    group: "Growth & confidence",
    bestFor: [
      "The inner critic that says you're not enough",
      "Imposter feelings before a milestone or new role",
      "Wanting a positive self-statement to actually land",
      "Building a steadier baseline of self-respect over time",
    ],
  },
  {
    slug: "hold_boundaries",
    title: "Hold Your Boundaries",
    hook: "Stand kindly in your own ground.",
    color: "#D98880",
    image: "/images/interventions/hold-boundaries.webp",
    group: "Growth & confidence",
    bestFor: [
      "Conversations where you freeze, soften, or back down",
      "Feeling guilty after saying no — or unable to say it at all",
      "Family, work, or partner dynamics that wear you thin",
      "Wanting to speak from steady ground, not from heat",
    ],
  },
  {
    slug: "relief_from_grief",
    title: "Relief From Grief",
    hook: "Soften the weight of loss.",
    color: "#9F7AEA",
    image: "/images/interventions/relief-from-grief.webp",
    group: "Emotional grounding",
    bestFor: [
      "Loss of a person, pet, relationship, or expected future",
      "Waves of grief that arrive without warning",
      "Wanting space to move forward without forgetting",
      "Self-help alongside grief support — not a replacement for it",
    ],
  },
  {
    slug: "calm_your_anger",
    title: "Calm Your Anger",
    hook: "Cool the heat without pushing it down.",
    color: "#78C0D8",
    image: "/images/interventions/calm-your-anger.webp",
    group: "Emotional grounding",
    bestFor: [
      "Anger or irritation that runs hotter than the moment called for",
      "Flashes of heat, a clenched jaw, a racing pulse",
      "Patterns you want to soften before reacting",
      "Responding from a calmer place — now and into the future",
    ],
  },
  {
    slug: "enhance_performance",
    title: "Enhance Your Performance",
    hook: "Show up focused when it matters.",
    color: "#3090A8",
    image: "/images/interventions/enhance-performance.webp",
    group: "Growth & confidence",
    bestFor: [
      "Athletes, performers, presenters, and high-stakes professionals",
      "Mental rehearsal for a specific moment",
      "Quieting the inner second-guess before showtime",
      "Syncing body and mind around the version of you that's ready",
    ],
  },
  {
    slug: "new_habit_new_you",
    title: "New Habit, New You",
    hook: "Anchor a lasting change.",
    color: "#E2B887",
    image: "/images/interventions/new-habit-new-you.webp",
    group: "Repair & transformation",
    bestFor: [
      "A habit you keep meaning to start — or stop — and can't make stick",
      "Intentions that fade by Wednesday",
      "Wanting the new pattern to feel familiar at a body level",
      "Pairing intention-setting with nervous-system support",
    ],
  },
  {
    slug: "nightmare_reset",
    title: "Nightmare Reset",
    hook: "Settle after a nightmare and rewrite it.",
    color: "#9F7AEA",
    image: "/images/interventions/nightmare-reset.webp",
    group: "Sleep health",
    bestFor: [
      "Lingering fear and held breath after a bad dream",
      "Dread of going back to sleep tonight",
      "Recurring nightmares with a familiar shape",
      "Helping your brain complete what REM got stuck on",
    ],
  },
  {
    slug: "fear_of_flying",
    title: "Fear Of Flying",
    hook: "Calm the dread before takeoff.",
    color: "#78C0D8",
    image: "https://customer-assets.emergentagent.com/job_calm-process/artifacts/80h47om1_john-mcarthur-PrdNTrIrG8w-unsplash.jpg",
    group: "Calm body",
    bestFor: [
      "A specific upcoming flight you'd love to actually enjoy",
      "Body-level dread that kicks in the moment you book a trip",
      "Turbulence, takeoff, or closed-cabin panic",
      "Loss of control that the cabin door closing seems to trigger",
    ],
  },
  {
    slug: "cpap_claustrophobia",
    title: "CPAP Mask Claustrophobia",
    hook: "Breathe easy with the mask on.",
    color: "#3090A8",
    image: "https://customer-assets.emergentagent.com/job_calm-process/artifacts/952bn3jh_woman-wearing-anti-snoring-chin-straps%20%281%29.jpg",
    group: "Sleep health",
    bestFor: [
      "Panic the moment the CPAP mask seals on your face",
      "The urge to rip the mask off in the middle of the night",
      "Dreading bedtime because of the mask",
      "Wanting the apnea treatment you need to actually feel restful",
    ],
    medicalDisclaimer:
      "Pair this with proper CPAP fitting and follow-up with your sleep specialist. This practice complements — not replaces — your medical care.",
  },
  {
    slug: "fear_of_small_places",
    title: "Fear Of Small Places",
    hook: "Stay calm in tight spaces.",
    color: "#E2B887",
    image: "https://images.unsplash.com/photo-1611940273499-e7e8ed5cbb55?auto=format&fit=crop&q=80&w=800",
    group: "Calm body",
    bestFor: [
      "An MRI scan or other lying-still procedure on the calendar",
      "Elevators, small rooms, crowded transit",
      "A 'walls closing in' sensation that fires fast",
      "Wanting to walk in without medication or sedation if possible",
    ],
  },
  {
    slug: "new_experience_anxiety",
    title: "Anxiety Of A New Experience",
    hook: "Show up without the dread.",
    color: "#9F7AEA",
    image: "https://customer-assets.emergentagent.com/job_calm-process/artifacts/3er0ns0m_mitchell-luo-1zTxyQ3kMaQ-unsplash%20%281%29.jpg",
    group: "Repair & transformation",
    bestFor: [
      "A new dance class, party, group, or meet-up you'd love to actually attend",
      "The urge to cancel and stay home that builds in the days before",
      "Self-consciousness about being the new person in the room",
      "Wanting to expand your life one new room at a time",
    ],
  },
  {
    slug: "resist_an_urge",
    title: "Resist An Urge",
    hook: "Soften the pull before you act on it.",
    color: "#D98880",
    image: "https://customer-assets.emergentagent.com/job_calm-process/artifacts/kysyitg9_bermix-studio-rGT7TZ7gBdo-unsplash.jpg",
    group: "Repair & transformation",
    bestFor: [
      "Late-evening snacking when you're not actually hungry",
      "Smoking, vaping, or drinking patterns you want to soften",
      "Doomscrolling, online shopping, or other habit loops",
      "Wanting to feel in charge of the choice instead of pulled into it",
    ],
    medicalDisclaimer:
      "For substance use disorders, eating disorders, or other conditions that benefit from clinical care, please work with a qualified professional. This practice is a self-help complement, not a substitute for treatment.",
  },
];

export const getPracticeBySlug = (slug) =>
  PRACTICE_CATALOG.find((p) => p.slug === slug) || null;
